import { ChakraProps, Input, useMultiStyleConfig } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { FormErrorMessageI18n } from "src/components/FormErrorMessageI18n/FormErrorMessageI18n";
import { Hx } from "src/components/Heading/Heading";
import { Textarea } from "src/components/Textarea/Textarea";
import { FormLabel, FormControl } from "src/components/forms";
import { useTranslatedString } from "src/i18n/hooks";
import { Laptop } from "src/icons";
import { z } from "src/lib/zod";
import { TypeOf } from "zod";
import {
	commonEmailPropsSchema,
	ContactForm,
	ContactFormProductSelect,
	ContactFormTitle,
} from "./ContactForm";

const demoSchema = commonEmailPropsSchema.extend({
	name: z.string(),
	email: z.string().email().min(1),
	company: z.string(),
	message: z.string().min(1).max(500),
});

export const DemoForm: React.FC<ChakraProps> = (props) => {
	const styles = useMultiStyleConfig("ContactForm");
	const t = useTranslatedString();

	const form = useForm<TypeOf<typeof demoSchema>>({
		resolver: zodResolver(demoSchema),
		mode: "onTouched",
		defaultValues: {
			emailSubject: t("contact.subject.demo"),
		},
	});

	const {
		register,
		control,
		formState: { errors, isValid },
	} = form;

	return (
		<ContactForm
			type="demo"
			isValid={isValid}
			buttonText={t("contact.send")}
			form={form}
			{...props}
		>
			<ContactFormTitle>
				<Laptop />
				<Hx size="h3">{t("contact.demo.title")}</Hx>
			</ContactFormTitle>
			<ContactFormProductSelect />
			<FormControl
				isInvalid={Boolean(errors.name)}
				sx={styles.formControl}
			>
				<FormLabel>{t("contact.form.name.label")}</FormLabel>

				<Input
					placeholder={t("contact.form.name.placeholder")}
					{...register("name")}
				/>

				<FormErrorMessageI18n error={errors.name} />
			</FormControl>

			<FormControl
				isInvalid={Boolean(errors.email)}
				sx={styles.formControl}
			>
				<FormLabel>{t("contact.form.email.label")}</FormLabel>

				<Input
					placeholder={t("contact.form.email.placeholder")}
					{...register("email")}
				/>

				<FormErrorMessageI18n error={errors.email} />
			</FormControl>

			<FormControl
				isInvalid={Boolean(errors.company)}
				sx={styles.formControl}
			>
				<FormLabel>{t("contact.form.company.label")}</FormLabel>

				<Input
					placeholder={t("contact.form.company.placeholder")}
					{...register("company")}
				/>

				<FormErrorMessageI18n error={errors.company} />
			</FormControl>

			<FormControl
				isInvalid={Boolean(errors.message)}
				sx={styles.formControl}
			>
				<FormLabel>{t("contact.form.message.label")}</FormLabel>

				<Controller
					control={control}
					name="message"
					render={({ field }) => (
						<Textarea
							maxLength={500}
							placeholder={t("contact.form.message.placeholder")}
							resize="none"
							{...field}
						/>
					)}
				/>

				<FormErrorMessageI18n error={errors.message} />
			</FormControl>
		</ContactForm>
	);
};

// 🔬 TBD: Please evaluate
